import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SiteHeader from '../components/wallet/SiteHeader';
import PageFooter from '../components/wallet/PageFooter';
import ErrorState from '../components/wallet/ErrorState';
import Add2WalletLogo from '../components/wallet/Add2WalletLogo';
import { preloadRecaptcha, getRecaptchaToken } from '../lib/recaptcha';

// JIT wallet lite-links Phase 4 (2026-08-28) — /join/:merchantId/:templateId,
// the open/anonymous QR-scan enrollment page. No pre-existing token or
// audience row: literally anyone with the URL/QR lands here, verifies their
// own mobile via a 6-digit SMS code, then either gets their existing pass
// (if already a member) or a fresh one.
//
// KNOWN GAP: send-code has no IP-level rate limit yet (docs/TODO.md #19) —
// the per-mobile throttle is built into the backend, IP abuse across many
// numbers is not yet covered.

const PHONE_PATTERN = /^\+?[0-9 ()-]{8,20}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'loading' | 'error' | 'enter-mobile' | 'enter-code' | 'done';

const JoinProgramLanding: React.FC = () => {
  const { merchantId, templateId } = useParams<{ merchantId: string; templateId: string }>();
  const [apiBase, setApiBase] = useState('');
  const [step, setStep] = useState<Step>('loading');
  const [errorMsg, setErrorMsg] = useState<{ title: string; message: string } | null>(null);
  const [orgName, setOrgName] = useState('');

  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  // "Ask the joiner" fields (2026-08-31) — merchant-configured, always
  // optional, custom prompt text. e.g. birthdate for birthday rewards.
  const [askFields, setAskFields] = useState<{ tag: string; prompt: string }[]>([]);
  const [tagAnswers, setTagAnswers] = useState<Record<string, string>>({});
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  useEffect(() => {
    // Warm the reCAPTCHA script now so the first "Send code" click doesn't
    // pay the load cost — this is the highest-risk endpoint on this page
    // (it triggers a real SMS send, at real cost, from a fully public URL).
    preloadRecaptcha();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/amplify_outputs.json')
      .then((r) => r.json())
      .then((config) => {
        if (cancelled) return;
        const endpoint = config?.custom?.API?.WalletHttpApi?.endpoint;
        setApiBase(typeof endpoint === 'string' ? endpoint.replace(/\/$/, '') : 'https://t7d9a6p352.execute-api.ap-southeast-2.amazonaws.com/prod');
      })
      .catch(() => {
        if (!cancelled) setApiBase('https://t7d9a6p352.execute-api.ap-southeast-2.amazonaws.com/prod');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!apiBase || !merchantId || !templateId) return;
    let cancelled = false;
    fetch(`${apiBase}/join/${encodeURIComponent(merchantId)}/${encodeURIComponent(templateId)}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 404) {
          setErrorMsg({ title: 'Not found', message: 'This membership link is invalid.' });
          setStep('error');
          return;
        }
        if (!r.ok) {
          setErrorMsg({ title: 'Something went wrong', message: 'We could not load this page right now.' });
          setStep('error');
          return;
        }
        const data = await r.json();
        setOrgName(data.organisation_name || '');
        setAskFields(Array.isArray(data.ask_fields) ? data.ask_fields : []);
        setStep('enter-mobile');
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMsg({ title: 'Something went wrong', message: 'We could not load this page right now.' });
          setStep('error');
        }
      });
    return () => { cancelled = true; };
  }, [apiBase, merchantId, templateId]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!PHONE_PATTERN.test(mobile.trim())) {
      setFieldError('Enter a valid mobile number.');
      return;
    }
    if (!EMAIL_PATTERN.test(email.trim())) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError(null);
    setBusy(true);
    try {
      const recaptcha_token = await getRecaptchaToken('join_send_code');
      const res = await fetch(`${apiBase}/join/${merchantId}/${templateId}/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: mobile.trim(), recaptcha_token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFieldError(data?.detail || data?.error || 'Something went wrong — please try again.');
        setBusy(false);
        return;
      }
      setStep('enter-code');
      setBusy(false);
    } catch {
      setFieldError('Something went wrong — please try again.');
      setBusy(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setFieldError('Enter the code we texted you.');
      return;
    }
    setFieldError(null);
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/join/${merchantId}/${templateId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: mobile.trim(), code: code.trim(), email: email.trim(), tag_answers: tagAnswers }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFieldError(data?.detail || data?.error || 'Something went wrong — please try again.');
        setBusy(false);
        return;
      }
      setResultUrl(data.url_path);
      setStep('done');
    } catch {
      setFieldError('Something went wrong — please try again.');
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step === 'done' && resultUrl) {
      window.location.replace(`/${resultUrl}`);
    }
  }, [step, resultUrl]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SiteHeader />
      <main className="flex-1">
        {step === 'loading' && (
          <div className="max-w-md mx-auto px-4 py-16 text-center text-gray-500 text-sm">Loading…</div>
        )}
        {step === 'error' && errorMsg && (
          <ErrorState title={errorMsg.title} message={errorMsg.message} canRetry={false} />
        )}
        {step === 'done' && (
          <div className="max-w-md mx-auto px-4 py-16 text-center text-gray-500 text-sm">Redirecting…</div>
        )}
        {step === 'enter-mobile' && (
          <div className="max-w-md mx-auto px-4 py-12">
            <h1 className="text-xl font-bold text-gray-900 mb-1">
              {orgName ? `Join ${orgName}` : 'Join the program'}
            </h1>
            <p className="text-sm text-gray-600 mb-6">Enter your mobile number and we'll text you a code.</p>
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile number</label>
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
                  placeholder="+61 4XX XXX XXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
                  placeholder="you@example.com"
                  required
                />
              </div>
              {askFields.map(({ tag, prompt }) => (
                <div key={tag}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{prompt} (optional)</label>
                  <input
                    type="text"
                    value={tagAnswers[tag] || ''}
                    onChange={(e) => setTagAnswers((prev) => ({ ...prev, [tag]: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
                  />
                </div>
              ))}
              {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center h-11 rounded-full bg-[#AEDD1F] text-[#06251B] text-sm font-semibold hover:bg-[#9CCB1B] transition disabled:opacity-60"
              >
                {busy ? 'Sending…' : 'Send code'}
              </button>
            </form>
          </div>
        )}
        {step === 'enter-code' && (
          <div className="max-w-md mx-auto px-4 py-12">
            <h1 className="text-xl font-bold text-gray-900 mb-1">Enter your code</h1>
            <p className="text-sm text-gray-600 mb-6">We texted a 6-digit code to {mobile}.</p>
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
                  placeholder="123456"
                  maxLength={6}
                />
              </div>
              {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center h-11 rounded-full bg-[#AEDD1F] text-[#06251B] text-sm font-semibold hover:bg-[#9CCB1B] transition disabled:opacity-60"
              >
                {busy ? 'Verifying…' : 'Verify & join'}
              </button>
            </form>
          </div>
        )}
        <div className="mt-10 flex justify-center opacity-60">
          {(step === 'enter-mobile' || step === 'enter-code') && <Add2WalletLogo size={20} />}
        </div>
      </main>
      <PageFooter />
    </div>
  );
};

export default JoinProgramLanding;
